import React, { useEffect } from "react";

import "bootstrap/dist/css/bootstrap.min.css";
import "./scss/custom.scss";

import { Toaster } from "react-hot-toast";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router";

import "./i18n/config";

import AgeTimeline from "./components/AgeTimeline";
import InitialLoader from "./components/Loader/InitialLoader";
import Map from "./components/Map";

import Basin from "./screens/basins/Basin";
import BasinsList from "./screens/basins/BasinList";
import Dashboard from "./screens/Dashboard";
import NotFound from "./screens/error/NotFound";
import Formation from "./screens/formations/Formation";
import FormationsList from "./screens/formations/FormationList";
import Location from "./screens/locations/Location";
import LocationsList from "./screens/locations/LocationsList";
import LogIn from "./screens/LogIn";
import { default as LogOut, default as LogOutConfirm } from "./screens/LogOut";
import User from "./screens/User";
import UsersList from "./screens/UsersList";

import { useDispatch, useSelector } from "react-redux";
import Drawer from "./components/Drawer";
import Extraction from "./screens/extractions/Extraction";
import ExtractionList from "./screens/extractions/ExtractionList";
import MassiveCreate from "./screens/MassiveCreate";
import Sample from "./screens/samples/Sample";
import SamplesList from "./screens/samples/SamplesList";
import { refreshToken } from "./services/authServices";
import httpService from "./services/httpService";
import { getAuthToken, resetAuthState, setAuthFromApi } from "./state/auth/authSlice";
import { routes, utils } from "./utils";
import { LocationsResults } from "./utils/types";

const ProtectedDashboardRoute = ({ element }: { element: JSX.Element }) => {
	if (!utils.isAdminOrCollaborator()) {
		return <Navigate to={routes.LOGIN} replace />;
	}

	return element;
};

function App() {
	const [ready, setReady] = React.useState<boolean>(false);
	const [mapLocations, setMapLocations] = React.useState<LocationsResults | undefined>();
	const [tokenChecked, setTokenChecked] = React.useState<boolean>(false);
	const [timelineDisplayed, setTimelineDisplayed] = React.useState<boolean>(true);

	const location = useLocation();
	const background = location.state && location.state.background;

	const dispatch = useDispatch();
	const token = useSelector(getAuthToken);

	const handleToken = async () => {
		try {
			if (token) {
				httpService.setAuthToken(token);
				const { access_token } = await refreshToken();
				if (access_token) {
					dispatch(setAuthFromApi(access_token));
				} else {
					dispatch(resetAuthState());
				}
			}
		} catch (error) {
			dispatch(resetAuthState());
		} finally {
			setTokenChecked(true);
		}
	};

	useEffect(() => {
		handleToken();
	}, []);

	return (
		<>
			<Toaster />
			<Routes location={background || location}>
				<Route
					path="/*"
					element={
						<>
							<Outlet />
							<InitialLoader visible={!ready} />
							{![routes.LOGIN, routes.LOGOUT].includes(location.pathname) && (
								<Drawer
									mapLocations={mapLocations}
									setMapLocations={setMapLocations}
									tokenChecked={tokenChecked}
								/>
							)}
							<Map locations={mapLocations} timelineDisplayed={timelineDisplayed} />
							<AgeTimeline
								visible={timelineDisplayed}
								setVisible={setTimelineDisplayed}
								onReady={() => setReady(true)}
								setTimelineDisplayed={setTimelineDisplayed}
							/>
						</>
					}
				/>
			</Routes>
			<Routes location={location}>
				<Route path="/login" element={<LogIn />} />
				<Route path="/logout" element={<LogOut />} />
				<Route path="/logout-confirm" element={<LogOutConfirm />} />
				<Route
					path="/dashboard/create-user"
					// eslint-disable-next-line react/jsx-no-bind
					element={<ProtectedDashboardRoute element={<User />} />}
				/>
				<Route
					path="/dashboard/create-basin"
					// eslint-disable-next-line react/jsx-no-bind
					element={<ProtectedDashboardRoute element={<Basin />} />}
				/>
				<Route
					path="/dashboard/basins-list"
					// eslint-disable-next-line react/jsx-no-bind
					element={<ProtectedDashboardRoute element={<BasinsList />} />}
				/>
				<Route
					path="/dashboard/basins-list/basin/:id"
					// eslint-disable-next-line react/jsx-no-bind
					element={<ProtectedDashboardRoute element={<Basin />} />}
				/>
				<Route
					path="/dashboard/create-formation"
					// eslint-disable-next-line react/jsx-no-bind
					element={<ProtectedDashboardRoute element={<Formation />} />}
				/>
				<Route
					path="/dashboard/formations-list"
					// eslint-disable-next-line react/jsx-no-bind
					element={<ProtectedDashboardRoute element={<FormationsList />} />}
				/>
				<Route
					path="/dashboard/formations-list/formation/:id"
					// eslint-disable-next-line react/jsx-no-bind
					element={<ProtectedDashboardRoute element={<Formation />} />}
				/>
				<Route
					path="/dashboard/users-list/user/:id"
					// eslint-disable-next-line react/jsx-no-bind
					element={<ProtectedDashboardRoute element={<User />} />}
				/>
				<Route
					path="/dashboard/user/:id"
					// eslint-disable-next-line react/jsx-no-bind
					element={<ProtectedDashboardRoute element={<User />} />}
				/>
				<Route
					path="/dashboard/users-list"
					// eslint-disable-next-line react/jsx-no-bind
					element={<ProtectedDashboardRoute element={<UsersList />} />}
				/>
				<Route
					path="/dashboard/create-location"
					// eslint-disable-next-line react/jsx-no-bind
					element={<ProtectedDashboardRoute element={<Location />} />}
				/>
				<Route
					path="/dashboard/locations-list"
					// eslint-disable-next-line react/jsx-no-bind
					element={<ProtectedDashboardRoute element={<LocationsList />} />}
				/>
				<Route
					path="/dashboard/locations-list/location/:id"
					// eslint-disable-next-line react/jsx-no-bind
					element={<ProtectedDashboardRoute element={<Location />} />}
				/>
				<Route
					path="/dashboard/add-location/:lat/lng/name"
					// eslint-disable-next-line react/jsx-no-bind
					element={<ProtectedDashboardRoute element={<Location />} />}
				/>
				<Route
					path="/dashboard/create-extraction"
					// eslint-disable-next-line react/jsx-no-bind
					element={<ProtectedDashboardRoute element={<Extraction />} />}
				/>
				<Route
					path="/dashboard/extractions-list/extraction/:id"
					// eslint-disable-next-line react/jsx-no-bind
					element={<ProtectedDashboardRoute element={<Extraction />} />}
				/>
				<Route
					path="/dashboard/extractions-list"
					// eslint-disable-next-line react/jsx-no-bind
					element={<ProtectedDashboardRoute element={<ExtractionList />} />}
				/>
				<Route
					path="/dashboard/create-sample"
					// eslint-disable-next-line react/jsx-no-bind
					element={<ProtectedDashboardRoute element={<Sample />} />}
				/>
				<Route
					path="/dashboard/samples-list"
					// eslint-disable-next-line react/jsx-no-bind
					element={<ProtectedDashboardRoute element={<SamplesList />} />}
				/>
				<Route
					path="/dashboard/upload-file"
					// eslint-disable-next-line react/jsx-no-bind
					element={<ProtectedDashboardRoute element={<MassiveCreate />} />}
				/>
				<Route
					path="/dashboard/samples-list/sample/:id"
					// eslint-disable-next-line react/jsx-no-bind
					element={<ProtectedDashboardRoute element={<Sample />} />}
				/>
				<Route
					path="/dashboard"
					// eslint-disable-next-line react/jsx-no-bind
					element={<ProtectedDashboardRoute element={<Dashboard />} />}
				/>
				<Route path="/not-found" element={<NotFound />} />
				<Route path="/" element={<></>} />
			</Routes>
		</>
	);
}

export default App;
